import React from 'react';
import { Box, IconButton, Popover, Stack, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EmojiFoodBeverageRoundedIcon from '@mui/icons-material/EmojiFoodBeverageRounded';
import { AlertTriangle, ChefHat } from 'lucide-react';
import { SearchableTagSelector } from '@/components/shared/SearchableTagSelector';
import { getValidFoodIntolerances } from '@/lib/clientPreferenceOptions';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { SpoonacularTagSummary } from '@/components/recipes/SpoonacularTagSummary';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';

type SearchResultMeta = {
  id?: string;
  name: string;
  description?: string | null;
  allergens?: string[];
  allergenTagIds?: string[];
  dietaryTagIds?: string[];
  dietLabels?: string[];
  intoleranceTagIds?: string[];
  intoleranceTags?: string[];
  tagClassificationStatus?: 'not_cached' | 'queued' | 'processing' | 'completed' | 'failed';
  tagClassificationSource?: 'preview' | 'ai' | 'none';
  tagClassificationUpdatedAt?: string | null;
  provider?: string;
  source?: string;
  sourceId?: string;
  type?: 'recipe' | 'ingredient';
  clinicalWarnings?: Array<{
    ruleId: string;
    severity: 'warning' | 'blocking';
    condition: string;
    trigger: string;
    message: string;
  }>;
};

const getSearchResultOptionKey = (item: SearchResultMeta): string => {
  const type = String(item.type ?? 'item').trim() || 'item';
  const id = String(item.id ?? '').trim();
  const sourceId = String(item.sourceId ?? '').trim();
  return `${type}:${id}:${sourceId}`;
};

const normalizeTagValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  values.forEach((value) => {
    const raw = String(value ?? '').trim();
    const key = raw.toLowerCase();
    if (!raw || seen.has(key)) return;
    seen.add(key);
    normalized.push(raw);
  });

  return normalized;
};

const normalizeDietaryToken = (value: string): string => (
  value.trim().toLowerCase().replace(/[-\s]+/g, '_')
);

const getAllergyDisplayLabel = (value: string): string => {
  const normalized = normalizeDietaryToken(value);
  return normalized ? getDietaryDisplayName(normalized, 'allergies') : value;
};

const formatAllergyList = (values: string[]): string => (
  values.map(getAllergyDisplayLabel).filter(Boolean).join(', ')
);

export type AddMealPlanItemPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  mealName: string | null;
  isSearching: boolean;
  isAdding?: boolean;
  addingOption?: string | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  options: string[];
  searchResults?: SearchResultMeta[];
  searchFilterAllergies?: string[];
  currentAllergies: string[];
  currentIntolerances: string[];
  onAddOption: (value: string) => void | Promise<void>;
  onCreateCustomRecipe?: () => void;
};

const AddMealPlanItemPopover: React.FC<AddMealPlanItemPopoverProps> = ({
  open,
  anchorEl,
  onClose: handleClose,
  mealName,
  isSearching,
  isAdding = false,
  addingOption = null,
  searchValue,
  onSearchChange,
  searchInputRef,
  options,
  searchResults = [],
  searchFilterAllergies = [],
  currentAllergies,
  currentIntolerances,
  onAddOption,
  onCreateCustomRecipe,
}) => {
  const resultByOption = React.useMemo(() => {
    const map = new Map<string, SearchResultMeta>();
    searchResults.forEach((item) => {
      const name = String(item.name ?? '').trim();
      if (!name) return;
      const optionKey = getSearchResultOptionKey(item);
      map.set(optionKey, item);
      map.set(optionKey.toLowerCase(), item);

      const nameKey = name.toLowerCase();
      const existing = map.get(nameKey);
      if (!existing || (!existing.description && item.description)) {
        map.set(nameKey, item);
      }
    });
    return map;
  }, [searchResults]);

  const normalizedSearchFilterAllergies = React.useMemo(
    () => normalizeTagValues(searchFilterAllergies),
    [searchFilterAllergies],
  );
  const normalizedCurrentAllergies = React.useMemo(
    () => normalizeTagValues(currentAllergies),
    [currentAllergies],
  );
  const allergySectionValues = normalizedCurrentAllergies.length
    ? normalizedCurrentAllergies
    : normalizedSearchFilterAllergies;
  const emptyOptionsText = React.useMemo(() => {
    if (!searchValue.trim()) return 'Type to search';
    if (!normalizedSearchFilterAllergies.length) return 'No matches found';
    return `Client has these allergies, so matching items cannot be shown: ${formatAllergyList(normalizedSearchFilterAllergies)}`;
  }, [normalizedSearchFilterAllergies, searchValue]);

  const showCreateCustomRecipe = Boolean(
    onCreateCustomRecipe
    && searchValue.trim()
    && options.length === 0
    && !isSearching
    && !isAdding,
  );

  const emptyOptionsExtra = showCreateCustomRecipe ? (
    <>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.75rem',
          color: '#98A2B3',
          textAlign: 'center',
          lineHeight: 1.4,
        }}
      >
        Create a custom recipe to add it to this meal.
      </Typography>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 border-dashed border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
        onClick={() => onCreateCustomRecipe?.()}
      >
        <ChefHat className="h-3.5 w-3.5 mr-1.5" />
        Create Custom Recipe
      </Button>
    </>
  ) : null;

  return (
  <Popover
    open={Boolean(open && anchorEl)}
    anchorEl={anchorEl}
    onClose={() => {
      if (isAdding) return;
      handleClose();
    }}
    disableRestoreFocus
    disableAutoFocus
    disableEnforceFocus
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    PaperProps={{
      sx: {
        mt: 0.5,
	        width: { xs: 'calc(100vw - 64px)', sm: 444 },
	        maxWidth: 'calc(100vw - 64px)',
	        maxHeight: 'calc(100vh - 96px)',
	        borderRadius: '8px',
	        overflowX: 'hidden',
	        overflowY: 'auto',
	        bgcolor: '#F7F7F7',
        border: '1px solid #E5E7EB',
        boxShadow: (theme) => theme.shadows[24],
      },
    }}
  >
    <Box sx={{ p: 0 }}>
      <Box
        sx={{
          px: 2.6,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #EAECF0',
          bgcolor: '#F7F7F7',
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              bgcolor: '#EAF7FC',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <EmojiFoodBeverageRoundedIcon sx={{ fontSize: 16, color: '#20AEDC' }} />
          </Box>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.02rem', fontWeight: 700, color: '#1F2937' }}>
            Add food or ingredient
          </Typography>
        </Stack>

        <IconButton
          onClick={() => {
            if (isAdding) return;
            handleClose();
          }}
          disabled={isAdding}
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#20AEDC',
            color: '#fff',
            '&:hover': { bgcolor: '#1297c4' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, py: 2.3, bgcolor: '#FFFFFF', borderTopLeftRadius: '14px', borderTopRightRadius: '14px' }}>
        {mealName ? (
          <Typography
            sx={{
              mt: 0,
              mb: 0.85,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 500,
              lineHeight: '16px',
              color: '#667085',
            }}
          >
            Adding to: {mealName}
          </Typography>
        ) : null}

        <SearchableTagSelector
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onSearchEnter={() => {}}
          searchInputRef={searchInputRef}
          autoFocus
          isSearching={isSearching}
          isAdding={isAdding}
          addingOption={addingOption}
          searchPlaceholder="Search food or ingredient"
          addSectionTitle="Add item"
          currentSectionTitle="Current Intolerances"
          filteredOptions={options}
          canAddCustom={false}
          customOptionLabel=""
          onAddOption={(value) => {
            const normalized = String(value ?? '').trim();
            const item = resultByOption.get(normalized) ?? resultByOption.get(normalized.toLowerCase());
            const blockingIssue = item?.clinicalWarnings?.find((warning) => warning.severity === 'blocking');
            if (blockingIssue?.message) {
              showErrorToast(blockingIssue.message, 'Clinically blocked');
              return;
            }
            const warning = item?.clinicalWarnings?.find((issue) => issue.severity === 'warning');
            if (warning?.message) {
              showWarningToast(warning.message, 'Clinical warning', { duration: 7000 });
            }
            void onAddOption(value);
          }}
          selectedValues={currentIntolerances}
          onRemoveSelected={() => {}}
          showRemoveSelected={false}
          resolveLabel={(value) => {
            const normalized = String(value ?? '').trim();
            if (!normalized) return normalized;
            const item = resultByOption.get(normalized) ?? resultByOption.get(normalized.toLowerCase());
            if (item) return item.name;
            return getValidFoodIntolerances().find(
              (option) => option.value.toLowerCase() === normalized.toLowerCase(),
            )?.name ?? normalized;
          }}
          renderOptionMeta={(value) => {
            const normalized = String(value ?? '').trim();
            const item = resultByOption.get(normalized) ?? resultByOption.get(normalized.toLowerCase());
            if (!item) return null;
            const description = typeof item.description === 'string' ? item.description.trim() : '';
            return (
              <>
                {description ? (
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.78rem',
                      lineHeight: '16px',
                      color: '#667085',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {description}
                  </Typography>
                ) : null}
                <RecipeAllergenCard
                  allergens={item.allergens ?? item.allergenTagIds}
                  compact
                  maxVisible={2}
                  className="mt-0.5 max-w-full"
                />
                <SpoonacularTagSummary item={item} className="mt-1" />
                {item.clinicalWarnings?.map((warning, warningIndex) => (
                  <Box
                    key={`${warning.ruleId || 'clinical-warning'}-${warningIndex}`}
                    sx={{
                      mt: 0.75,
                      px: 1,
                      py: 0.7,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.7,
                      borderRadius: '6px',
                      border: warning.severity === 'blocking' ? '1px solid #fca5a5' : '1px solid #fcd34d',
                      bgcolor: warning.severity === 'blocking' ? '#fef2f2' : '#fffbeb',
                      color: warning.severity === 'blocking' ? '#991b1b' : '#78350f',
                    }}
                  >
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.72rem',
                        lineHeight: '16px',
                        color: 'inherit',
                      }}
                    >
                      {warning.severity === 'blocking' ? 'Blocked: ' : ''}{warning.message || 'Review this item for the client’s clinical condition.'}
                    </Typography>
                  </Box>
                ))}
              </>
            );
          }}
          emptyOptionsText={emptyOptionsText}
          emptyOptionsExtra={emptyOptionsExtra}
          emptySelectedText="No intolerances added yet"
          addAriaLabel={(displayLabel) => `Add ${displayLabel}`}
          removeAriaLabel={(displayLabel) => `Remove ${displayLabel} intolerance`}
          listMaxHeight={250}
          appearance="ingredient-modal"
        />

        {allergySectionValues.length ? (
          <>
            <Box sx={{ mt: 2.0, borderTop: '1px solid #EAECF0' }} />

            <Typography
              sx={{
                mt: 1.6,
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                fontWeight: 700,
                color: '#1E2939',
              }}
            >
              Current Allergies
            </Typography>

            <Box
              sx={{
                mt: 0.9,
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 0.8,
              }}
            >
              {allergySectionValues.map((value) => {
                const displayLabel = getAllergyDisplayLabel(value);
                return (
                  <Box key={value} sx={{ minWidth: 0 }}>
                    <Tag
                      variant="outlined"
                      size="md"
                      maxWidth="100%"
                      className="w-full justify-between rounded-lg border-[#D0D5DD] bg-white font-[Inter,sans-serif] font-medium text-[#344054]"
                    >
                      {displayLabel}
                    </Tag>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  </Popover>
  );
};

export default AddMealPlanItemPopover;
